import { useLocale } from 'locale';
import React, { useEffect, useState } from 'react';
import { ErrMsg, FieldWrapper, FormPreloader, FormWrapper, HalfSection, IconWrapper, OkIconWrapper, TermsLink } from './styled';
import { Button } from 'ds/components/button';
import { SpacingBlock } from 'components/spacing-block';
import QIcon from 'assets/svg/q.svg';
import OkIcon from 'assets/svg/ok-icon-green.svg';
import Tooltip from 'ds/components/tooltip';
import { LogoLoader } from './LogoLoader';
import { TermsPageSectionId, TermsPageSource } from 'screens/TermsPage/types';
import { useTermsPageHref } from 'screens/TermsPage/useTermsPageHref';
import { tranzilaHostedFieldsService } from 'utils/tranzilaHostedFieldsService';
import { logAndTrackError } from 'helpers/jstracking';

const READY_FALLBACK_TIMEOUT_MS = 10000;


interface Props {
  handleSubmit: () => void;
  cardError: boolean | null;
  cvvError: boolean | null;
  dateError: boolean | null;
  holderError: boolean | null;
  amount: number | undefined;
}

enum ErrorMessage {
  CardValidationError = 'cardValidationError',
  CVVValidationError = 'cvvValidationError',
  DateValidationError = 'dateValidationError',
  CardHolderIdError = 'cardHolderIdError',
}

const FieldError: React.FC<{ message?: string }> = ({ message }) => {
  if (!message) return null;
  return <ErrMsg>{message}</ErrMsg>;
};

const PaymentForm: React.FC<Props> = ({
  handleSubmit,
  cardError,
  cvvError,
  dateError,
  holderError,
  amount,
}) => {
  const { t } = useLocale();
  const [ isPreloading, setIsPreloading ] = useState(true);
  const termsOfServiceHref = useTermsPageHref({
    source: TermsPageSource.PromotionPlanPaymentForm,
    sectionId: TermsPageSectionId.PrivateListingPromotionPlanTerms,
  });

  useEffect(() => {
    let settled = false;

    const stopPreloading = () => {
      if (settled) return;
      settled = true;
      setIsPreloading(false);
    };

    const fallbackTimeoutId = setTimeout(() => {
      if (settled) return;
      logAndTrackError(
        `HOSTED FIELDS: Tranzila ready event not received within ${READY_FALLBACK_TIMEOUT_MS}ms, hiding loader via fallback timeout`
      );
      stopPreloading();
    }, READY_FALLBACK_TIMEOUT_MS);

    const unsubscribe = tranzilaHostedFieldsService.onReady(stopPreloading);

    return () => {
      clearTimeout(fallbackTimeoutId);
      unsubscribe();
    };
  }, []);

  const disabled = cardError === null || cvvError === null || dateError === null || holderError === null;

  const onSubmit = (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit();
  };

  return (
    <>
      <FormWrapper onSubmit={onSubmit} dir="rtl">
        {isPreloading ? (
          <FormPreloader>
            <LogoLoader />
          </FormPreloader>
        ) : null}
        <FieldWrapper>
          <div id="credit_card_num">
            <IconWrapper>
              {cardError === false ? (
                <OkIconWrapper>
                  <OkIcon />
                </OkIconWrapper>
              ) : null}
            </IconWrapper>
          </div>
          <FieldError message={cardError && t('tranzila.hostedFields.form.error', { msg: ErrorMessage.CardValidationError })} />
        </FieldWrapper>
        <HalfSection>
          <FieldWrapper className="half">
            <div id="expiry" className="hosted-input">
              <IconWrapper>
                {dateError === false ? (
                  <OkIconWrapper>
                    <OkIcon />
                  </OkIconWrapper>
                ) : null}
              </IconWrapper>
            </div>
            <FieldError message={dateError && t('tranzila.hostedFields.form.error', { msg: ErrorMessage.DateValidationError })} />
          </FieldWrapper>

          <FieldWrapper className="half">
            <div id="cvv" className="hosted-input">
              <IconWrapper>
                <Tooltip
                  size="medium"
                  placement="bottom"
                  tooltip={t('tranzila.hostedFields.form.tooltip')}
                  tapThreshold={0}
                  wrapperStyle={{ zIndex: 1002, fontSize: 13, lineHeight: '20px' }}
                >
                  <QIcon />
                </Tooltip>
                {cvvError === false ? (
                  <OkIconWrapper>
                    <OkIcon />
                  </OkIconWrapper>
                ) : null}
              </IconWrapper>
            </div>
            <FieldError message={cvvError && t('tranzila.hostedFields.form.error', { msg: ErrorMessage.CVVValidationError })} />
          </FieldWrapper>
        </HalfSection>
        <FieldWrapper>
          <div id="card_holder_id_number" className="hosted-input">
            <IconWrapper>
              {holderError === false ? (
                <OkIconWrapper>
                  <OkIcon />
                </OkIconWrapper>
              ) : null}
            </IconWrapper>
          </div>
          <FieldError message={holderError && t('tranzila.hostedFields.form.error', { msg: ErrorMessage.CardHolderIdError })} />
        </FieldWrapper>
        <SpacingBlock mTop={3} />
        <TermsLink>
          <span>{t('tranzila.hostedFields.form.text')}</span>
          <a href={termsOfServiceHref} target="_blank" rel="noopener noreferrer">{t('tranzila.hostedFields.form.hrefText')}</a>
        </TermsLink>
        <SpacingBlock mTop={3} />
        <Button size="extraLarge" mode="primary" fullWidth disabled={cardError || cvvError || dateError || holderError || disabled}>{t('tranzila.hostedFields.form.button', { amount })}</Button>
      </FormWrapper>
    </>
  );
};

export default PaymentForm;
